import React, { useState, useEffect, useRef } from 'react';
import { fetchPitchHistory } from '../api/pitchService';

function TablePitchHistory() {
  const [pitches, setPitches] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(30);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [activeReason, setActiveReason] = useState(null);
  const [isNotificationVisible, setIsNotificationVisible] = useState(false);
  const [message, setMessage] = useState('');
  const tooltipRef = useRef(null);
  const totalPages = Math.ceil(totalCount / limit);

  function fetchData() {
    setIsPageLoading(true);
    
    fetchPitchHistory({
      page,
      limit,
      companyName: searchTerm,
      startDate,
      endDate,
    })
      .then((data) => {
        setPitches(data.items || []);
        setTotalCount(data.total_count || 0);
      })
      .catch((err) => {
        console.error('Error fetching history:', err);
        setMessage('Error fetching pitch history. Please try again.');
      })
      .finally(() => {
        setTimeout(() => {
          setIsPageLoading(false);
          setIsLoading(false);
          
          const tableElement = document.querySelector('.pitches-history-container');
          if (tableElement) {
            tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      });
  }

  useEffect(() => {
    const handleClickOutside = () => {
      if (activeReason) setActiveReason(null);
    };
  
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, [activeReason]);

  useEffect(() => {
    fetchData();
  }, [page, searchTerm, startDate, endDate]);

  useEffect(() => {
    if (message) {
      setIsNotificationVisible(true);
      
      const timer = setTimeout(() => {
        setIsNotificationVisible(false);
        
        setTimeout(() => {
          setMessage('');
        }, 500);
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setActiveReason(null);
      }
    };
  
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
    setPage(1);
  };

  const handleStartDateChange = (e) => {
    setStartDate(e.target.value);
    setPage(1);
  };

  const handleEndDateChange = (e) => {
    setEndDate(e.target.value);
    setPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    
    const date = new Date(dateStr);
    return date.toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleToggleReason = (e, reasonText) => {
    e.stopPropagation();
  
    const tooltipWidth = 300;
    const tooltipHeight = 200;
    const padding = 10;
  
    const pageX = e.pageX;
    const pageY = e.pageY;
    const clientY = e.clientY;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
  
    const spaceBelow = viewportHeight - clientY;
    const showAbove = spaceBelow < tooltipHeight + padding;
  
    let top = showAbove
      ? pageY - tooltipHeight - padding + 10
      : pageY + padding - 80;
  
    let left = pageX - tooltipWidth / 2 - 30;
  
    if (left + tooltipWidth > viewportWidth) {
      left = viewportWidth - tooltipWidth - padding;
    }
    if (left < padding) {
      left = padding;
    }
  
    if (activeReason && activeReason.text === reasonText) {
      setActiveReason(null);
    } else {
      setActiveReason({
        text: reasonText,
        position: { top, left }
      });
    }
  };
  

  const handlePrevPage = () => {
    if (page > 1 && !isPageLoading) {
      setPage(prev => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages && !isPageLoading) {
      setPage(prev => prev + 1);
    }
  };

  const infoIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '18px',
    height: '18px',
    borderRadius: '50%',
    backgroundColor: '#1890ff',
    color: 'white',
    fontWeight: 'bold',
    fontSize: '12px',
    marginLeft: '8px',
    cursor: 'pointer'
  };

  const statusStyle = (status) => {
    if (status === 'Won') {
      return { 
        backgroundColor: '#f6ffed', 
        color: '#52c41a',
        fontWeight: 'bold',
        padding: '4px 8px',
        borderRadius: '4px',
        display: 'inline-flex',
        alignItems: 'center'
      };
    } else if (status === 'Lost') {
      return { 
        backgroundColor: '#fff1f0', 
        color: '#ff4d4f',
        fontWeight: 'bold',
        padding: '4px 8px',
        borderRadius: '4px',
        display: 'inline-flex',
        alignItems: 'center'
      };
    }
    return {};
  };

  const ReasonTooltip = ({ reason }) => {
    if (!reason) return null;
  
    return (
      <div
        ref={tooltipRef}
        style={{
          position: 'absolute',
          top: reason.position.top + 20,
          left: reason.position.left,
          width: '300px',
          padding: '8px 12px',
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          zIndex: 1000,
          fontSize: '14px',
          maxHeight: '200px',
          overflowY: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {reason.text}
      </div>
    );
  };
  

  return (
    <div className="pitches-history-container" style={{ position: 'relative' }}>
      <h2>History (Won &amp; Lost Pitches)</h2>
      {message && (
        <div className="message"
          style={{ 
            opacity: isNotificationVisible ? 1 : 0,
          }}
        >
          {message}
        </div>
      )}

      {activeReason && (
        <ReasonTooltip reason={activeReason} />
      )}

      <div style={{ 
        background: '#ffffff', 
        borderRadius: '8px', 
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        padding: '1.5rem',
        marginBottom: '1.5rem'
      }}>
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: '1rem', 
          marginBottom: '1.5rem',
          padding: '0 0 1rem 0',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label htmlFor="company-search">Company:</label>
            <input
              id="company-search"
              className="small-input"
              type="text"
              placeholder="Search by name..."
              value={searchTerm}
              onChange={handleSearchTermChange}
              style={{ 
                padding: '8px', 
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label htmlFor="start-date">From:</label>
            <input
              id="start-date"
              className="small-input"
              type="date"
              value={startDate}
              onChange={handleStartDateChange}
              style={{ 
                padding: '8px', 
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <label htmlFor="end-date">To:</label>
            <input
              id="end-date"
              className="small-input"
              type="date"
              value={endDate}
              onChange={handleEndDateChange}
              style={{ 
                padding: '8px', 
                borderRadius: '4px',
                border: '1px solid #d9d9d9'
              }}
            />
          </div>

          <button 
            className="button-bin"
            onClick={handleClearFilters}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
              strokeWidth="2"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear Filters
          </button>
        </div>

        {isLoading && !isPageLoading && (
          <div style={{ 
            marginTop: '1rem', 
            marginBottom: '1rem',
            display: 'flex',
            alignItems: 'center'
          }}>
            <div style={{ 
              width: '20px', 
              height: '20px', 
              border: '3px solid rgba(0, 0, 0, 0.1)', 
              borderRadius: '50%', 
              borderTop: '3px solid #3498db', 
              animation: 'spin 1s linear infinite',
              marginRight: '10px'
            }}></div>
            <span>Loading pitches...</span>
          </div>
        )}

        <div style={{ 
          overflowX: 'auto',
          overflowY: 'visible',
          maxWidth: '100%',
          position: 'relative'
        }}>
          {isPageLoading && (
            <div style={{ 
              textAlign: 'center', 
              padding: '1rem',
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 10,
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
              <div style={{ 
                width: '30px', 
                height: '30px', 
                border: '3px solid rgba(0, 0, 0, 0.1)', 
                borderRadius: '50%', 
                borderTop: '3px solid #1890ff', 
                animation: 'spin 1s linear infinite' 
              }}></div>
            </div>
          )}

          {!isLoading && !isPageLoading && pitches.length === 0 && (
            <div style={{ 
              padding: '2rem', 
              textAlign: 'center', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '4px',
              marginTop: '1rem',
              marginBottom: '1rem'
            }}>
              No pitches found with the current filters.
            </div>
          )}

          {!isLoading && pitches.length > 0 && (
            <table>
              <thead>
                <tr style={{ backgroundColor: '#fafafa', borderBottom: '2px solid #f0f0f0' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Company Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Agent Name</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Agent Email</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Sent Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {pitches.map((pitch, index) => (
                  <tr key={pitch.pitch_id || index} style={{
                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#fafafa',
                    borderBottom: '1px solid #f0f0f0'
                  }}>
                    <td style={{ padding: '12px 16px' }}>{pitch.company_name}</td>
                    <td style={{ padding: '12px 16px' }}>{pitch.agent_name}</td>
                    <td style={{ padding: '12px 16px' }}>{pitch.agent_email}</td>
                    <td style={{ padding: '12px 16px' }}>{formatDate(pitch.sent_at)}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={statusStyle(pitch.status)}>
                        {pitch.status}
                        {pitch.reason && (
                          <span 
                            style={infoIconStyle}
                            onClick={(e) => handleToggleReason(e, pitch.reason)}
                          >
                            i
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {!isLoading && pitches.length > 0 && (
        <div 
          className="pagination-container"
        >
          <button
            className="pagination-button"
            disabled={page <= 1 || isPageLoading}
            onClick={() => setPage(1)}
            title="First Page"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
            </svg>
          </button>

          <button
            className="pagination-button"
            onClick={handlePrevPage}
            disabled={page <= 1 || isPageLoading}
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Previous
          </button>

          <div>
            <span>Page {page} of {totalPages || 1}</span>
          </div>

          <button
            className="pagination-button"
            onClick={handleNextPage}
            disabled={page >= totalPages || totalPages === 0 || isPageLoading}
          >
            Next
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          <button
            className="pagination-button"
            disabled={page >= totalPages || totalPages === 0 || isPageLoading}
            onClick={() => setPage(totalPages)}
            title="Last Page"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="16" 
              height="16" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7m-8-14l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
}

export default TablePitchHistory;